/**
 * Stage 5 — Word Garden.
 *
 * Two phases:
 *
 *   Phase 1: Discovery — 5 "seed" cards, tap one to grow it into a
 *            flower revealing the Arabic word, its image, and the
 *            audio button. Listen & learn.
 *
 *   Phase 2: Quiz — once all seeds are revealed, 5 rounds of
 *            picture-match: see an image, pick the matching word
 *            out of 4 options (target + 3 distractors from already
 *            revealed vocabulary).
 *
 * Scoring: 5/5 correct = 3 stars, 4/5 = 2, 3/5 = 1, else 0.
 */

import { useEffect, useState } from 'preact/hooks';
import { useLocation, useRoute } from 'preact-iso';
import { api } from '../lib/api';
import { playUrl, unlockAudio } from '../lib/audio';
import { currentUser } from '../state/user';
import { refreshMap } from '../state/content';
import { Spinner } from '../components/Spinner';
import { Celebration } from '../components/Celebration';
import { NavButton } from '../components/NavButton';
import type { Word } from '@huroof/shared';

const QUIZ_ROUNDS = 5;

interface QuizRound {
  target: Word;
  options: Word[];
}

type Phase = 'discovery' | 'quiz' | 'done';

export function StageGarden() {
  const { route } = useLocation();
  const { params } = useRoute();
  const letterId = Number(params.id);

  const [words, setWords] = useState<Word[]>([]);
  const [discovered, setDiscovered] = useState<Set<number>>(new Set());
  const [phase, setPhase] = useState<Phase>('discovery');
  const [roundIdx, setRoundIdx] = useState(0);
  const [rounds, setRounds] = useState<QuizRound[]>([]);
  const [picked, setPicked] = useState<number | null>(null);
  const [correctCount, setCorrectCount] = useState(0);
  const [wrongResults, setWrongResults] = useState<boolean[]>([]);
  const [showHint, setShowHint] = useState(false);

  useEffect(() => {
    unlockAudio();
    api<{ ok: true; words: Word[] }>(`/api/letters/${letterId}/words`).then(
      (r) => setWords(r.words),
    );
  }, [letterId]);

  function grow(w: Word) {
    setDiscovered((prev) => new Set(prev).add(w.id));
    void playUrl(w.audio_url);
  }

  function startQuiz() {
    // Build 5 rounds of picture-match using the discovered words.
    // Each round picks one target + 3 distractors from the remaining.
    const shuffled = [...words].sort(() => Math.random() - 0.5);
    const pool = shuffled.length >= 4 ? shuffled : [...shuffled, ...shuffled, ...shuffled, ...shuffled];
    const rs: QuizRound[] = [];
    for (let i = 0; i < QUIZ_ROUNDS; i++) {
      const target = pool[i % pool.length]!;
      const distractors = pool
        .filter((w) => w.id !== target.id)
        .sort(() => Math.random() - 0.5)
        .slice(0, 3);
      const options = [target, ...distractors].sort(() => Math.random() - 0.5);
      rs.push({ target, options });
    }
    setRounds(rs);
    setRoundIdx(0);
    setPicked(null);
    setCorrectCount(0);
    setPhase('quiz');
  }

  function pickAnswer(wordId: number) {
    if (picked !== null) return;
    const round = rounds[roundIdx]!;
    const correct = wordId === round.target.id;
    setPicked(wordId);
    if (correct) setCorrectCount((c) => c + 1);
    setWrongResults((prev) => [...prev, !correct]);
    try {
      navigator.vibrate?.(correct ? 25 : [15, 30, 15]);
    } catch {
      /* unsupported */
    }
    // Play the word audio as reinforcement.
    setTimeout(() => void playUrl(round.target.audio_url), 200);
    setTimeout(() => {
      if (roundIdx + 1 >= QUIZ_ROUNDS) finish();
      else {
        // If the last two rounds were both wrong, show a hint on the
        // next round. This surfaces only briefly (via showHint below).
        const recent = [...wrongResults, !correct];
        const hint =
          recent.length >= 2 &&
          recent[recent.length - 1] === true &&
          recent[recent.length - 2] === true;
        setShowHint(hint);
        if (hint) setTimeout(() => setShowHint(false), 2500);
        setRoundIdx(roundIdx + 1);
        setPicked(null);
      }
    }, 1400);
  }

  async function finish() {
    setPhase('done');
    const stars =
      correctCount >= 5 ? 3 : correctCount >= 4 ? 2 : correctCount >= 3 ? 1 : 0;

    if (currentUser.value) {
      try {
        await api('/api/progress', {
          method: 'POST',
          body: {
            letterId,
            stage: 5,
            stars,
            attempts: QUIZ_ROUNDS,
            errors: [],
          },
        });
        void refreshMap();
      } catch {
        // non-fatal
      }
    }
    setTimeout(() => route(`/island/${letterId}`), 1600);
  }

  if (words.length === 0) {
    return (
      <div class="flex-1 grid place-items-center">
        <Spinner label="جَارٍ زَرْعُ البُذُورِ…" labelEn="Planting seeds…" />
      </div>
    );
  }

  if (phase === 'discovery') {
    const allDiscovered = discovered.size >= words.length;
    return (
      <div class="flex-1 px-4 py-6 pb-10 max-w-md mx-auto w-full">
        <header class="flex items-center justify-between mb-4">
          <NavButton ar="← إِنْهَاءٌ" en="Exit" onClick={() => route(`/island/${letterId}`)} />
          <div class="text-sm text-ink/60">
            {discovered.size}/{words.length}
          </div>
        </header>

        <div class="text-center mb-4">
          <h2 class="text-2xl font-display text-leaf">حَدِيقَةُ الكَلِمَاتِ</h2>
          <p class="text-[10px] text-ink/40" dir="ltr">Word garden — tap a seed to grow it</p>
        </div>

        <div class="grid grid-cols-2 gap-3">
          {words.map((w) => {
            const isGrown = discovered.has(w.id);
            return (
              <button
                key={w.id}
                type="button"
                onClick={() => grow(w)}
                class={`rounded-3xl p-3 shadow-lg transition-transform active:scale-95
                        ${isGrown ? 'bg-white' : 'bg-leaf text-white hover:scale-[1.02]'}`}
              >
                {isGrown ? (
                  <>
                    <img
                      src={w.image_url}
                      alt={w.meaning_en}
                      class="w-full aspect-square rounded-2xl object-cover bg-sand"
                      loading="lazy"
                    />
                    <p class="mt-2 text-lg font-display text-coral">{w.word_ar}</p>
                    <p class="text-xs text-ink/50" dir="ltr">
                      {w.meaning_en}
                    </p>
                  </>
                ) : (
                  <div class="aspect-square grid place-items-center">
                    <span class="text-5xl" aria-hidden="true">
                      🌱
                    </span>
                    <span class="mt-1 text-xs" dir="ltr">
                      tap to grow
                    </span>
                  </div>
                )}
              </button>
            );
          })}
        </div>

        {allDiscovered && (
          <button
            type="button"
            onClick={startQuiz}
            class="mt-6 w-full min-h-[64px] px-8 py-3 rounded-2xl bg-coral text-white shadow-xl shadow-coral/40 active:scale-95 flex flex-col items-center"
          >
            <span class="text-xl font-display">هَيَّا بِنَا نَلْعَبْ!</span>
            <span class="text-[10px] opacity-85" dir="ltr">Let's play!</span>
          </button>
        )}
      </div>
    );
  }

  if (phase === 'quiz') {
    const round = rounds[roundIdx];
    if (!round) return null;

    return (
      <div class="flex-1 px-4 py-6 pb-10 max-w-md mx-auto w-full flex flex-col">
        <header class="flex items-center justify-between mb-4">
          <NavButton ar="← إِنْهَاءٌ" en="Exit" onClick={() => route(`/island/${letterId}`)} />
          <div class="flex gap-1">
            {Array.from({ length: QUIZ_ROUNDS }).map((_, i) => (
              <span
                key={i}
                class={`h-2 w-8 rounded-full ${
                  i < roundIdx ? 'bg-leaf' : i === roundIdx ? 'bg-coral' : 'bg-ink/15'
                }`}
              />
            ))}
          </div>
        </header>

        <div class="text-center">
          <p class="text-sm font-display text-ink/60">مَا هٰذِهِ الصُّورَةُ؟</p>
          <p class="text-[10px] text-ink/40" dir="ltr">What is this picture?</p>
        </div>

        <img
          src={round.target.image_url}
          alt=""
          class="w-full max-w-sm mx-auto aspect-square rounded-3xl object-cover bg-white shadow-lg my-6"
          loading="eager"
        />

        <div class="grid grid-cols-2 gap-3 mt-auto">
          {round.options.map((opt) => {
            const isPicked = picked === opt.id;
            const isTarget = opt.id === round.target.id;
            const showCorrect = picked !== null && isTarget;
            const showWrong = isPicked && !isTarget;
            const showAsHint = showHint && picked === null && isTarget;
            return (
              <button
                key={opt.id}
                type="button"
                disabled={picked !== null}
                onClick={() => pickAnswer(opt.id)}
                class={`min-h-[80px] rounded-2xl font-display text-2xl shadow-lg
                        ${
                          showCorrect
                            ? 'bg-leaf text-white animate-bounce'
                            : showWrong
                              ? 'bg-berry text-white animate-shake'
                              : showAsHint
                                ? 'bg-white text-coral animate-hint-glow ring-4 ring-leaf/40'
                                : 'bg-white text-coral active:scale-95'
                        }`}
              >
                {opt.word_ar}
              </button>
            );
          })}
        </div>

        {showHint && picked === null && (
          <p class="text-xs text-leaf mt-3 font-display text-center">
            هٰذِهِ هِيَ
            <span class="ms-2 opacity-60" dir="ltr">
              — this one's it
            </span>
          </p>
        )}
      </div>
    );
  }

  // done
  const stars =
    correctCount >= 5 ? 3 : correctCount >= 4 ? 2 : correctCount >= 3 ? 1 : 0;
  return <Celebration stars={stars} scoreText={`${correctCount}/${QUIZ_ROUNDS}`} />;
}
